import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, finalize } from 'rxjs';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../../../shared/components/empty-result/empty-result.component';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption,
} from '../../../shared/components/date-range-picker/date-range-picker.component';
import {
  RewardsService,
  RewardsHistoryItem,
  RewardsFilters,
  RewardsPagination,
  RewardStatistics,
} from '../../../services/rewards.service';
import {
  formatCalendarDateInTimeZone,
  formatShortDate,
  getUserTimezone,
} from '../../../shared/utils/date.util';
import { getDateRangeForPeriod } from '../../utils/rewards-period.util';
import {
  REWARDS_DEFAULT_PAGINATION,
  REWARDS_DEFAULT_PERIOD,
  REWARDS_ITEMS_PER_PAGE_OPTIONS,
  REWARDS_PERIOD_OPTIONS,
  REWARDS_TOAST_LIFETIME,
} from '../../utils/rewards-page.constants';

const DEFAULT_REWARD_VALUES = {
  WASH_COUNT: 50,
  THRESHOLD_REWARD: 3,
  MEMBERSHIP_REWARD: 5,
} as const;

const ERROR_MESSAGES = {
  STATISTICS: 'Failed to load reward statistics. Please try again.',
  HISTORY: 'Failed to load rewards history. Please try again.',
} as const;

@Component({
  selector: 'app-rewards-v1',
  standalone: true,
  imports: [CommonModule, PaginationBarComponent, EmptyResultComponent, DateRangePickerComponent],
  providers: [NotificationService],
  templateUrl: './rewards-v1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewards-v1.component.scss'],
})
export class RewardsV1Component implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isLoading = false;
  selectedPeriod: string = REWARDS_DEFAULT_PERIOD;
  isDateRangePickerOpen = false;
  customDateRange: DateRange = { startDate: null, endDate: null };
  readonly periodOptions: PeriodOption[] = REWARDS_PERIOD_OPTIONS;

  lifetimeRewards = '$0.00';
  paginationData: PaginationData = { ...REWARDS_DEFAULT_PAGINATION };
  readonly itemsPerPageOptions: number[] = [...REWARDS_ITEMS_PER_PAGE_OPTIONS];

  singleWashProgress: {
    current: number;
    total: number;
    reward: string;
    wonToday: string;
  } = {
    current: 0,
    total: DEFAULT_REWARD_VALUES.WASH_COUNT,
    reward: `$${DEFAULT_REWARD_VALUES.THRESHOLD_REWARD} for ${DEFAULT_REWARD_VALUES.WASH_COUNT} washes`,
    wonToday: '$0 won today',
  };

  membershipProgress = {
    reward: `$${DEFAULT_REWARD_VALUES.MEMBERSHIP_REWARD} for each membership`,
    description: 'Sell any membership.',
    wonToday: '$0 won today',
  };

  rewardsHistory: RewardsHistoryItem[] = [];

  constructor(
    private readonly rewardsService: RewardsService,
    private readonly messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadRewardsHistory();
    this.loadRewardStatistics();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDateRangePicker(): void {
    this.isDateRangePickerOpen = !this.isDateRangePickerOpen;
  }

  closeDateRangePicker(): void {
    this.isDateRangePickerOpen = false;
  }

  onDateRangePeriodChange(period: string): void {
    this.selectedPeriod = period;
  }

  onCustomDateRangeChange(dateRange: DateRange): void {
    this.customDateRange = dateRange;
  }

  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    this.selectedPeriod = data.period;
    if (data.customRange) {
      this.customDateRange = data.customRange;
    }
    this.closeDateRangePicker();
    this.filterDataByPeriod();
  }

  onPageChange(page: number): void {
    this.paginationData.currentPage = page;
    this.loadRewardsHistory();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.paginationData.itemsPerPage = itemsPerPage;
    this.paginationData.currentPage = 1;
    this.loadRewardsHistory();
  }

  formatDate(dateString: string): string {
    return formatShortDate(dateString);
  }

  trackByIndex(index: number): number {
    return index;
  }

  private loadRewardStatistics(): void {
    this.rewardsService
      .getRewardStatistics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: stats => this.handleStatisticsSuccess(stats),
        error: error => this.handleStatisticsError(error),
      });
  }

  private handleStatisticsSuccess(stats: RewardStatistics): void {
    const rewardSettings = stats.reward;
    const membershipReward =
      rewardSettings?.membership_reward ?? DEFAULT_REWARD_VALUES.MEMBERSHIP_REWARD;
    const washCount = rewardSettings?.wash_count ?? DEFAULT_REWARD_VALUES.WASH_COUNT;
    const thresholdReward =
      rewardSettings?.threshold_reward ?? DEFAULT_REWARD_VALUES.THRESHOLD_REWARD;

    const totalLifetimeRewards =
      (stats.total?.subscription ?? 0) + (stats.total?.threshold_reward ?? 0);
    this.lifetimeRewards = this.formatCurrency(totalLifetimeRewards);

    const currentWashCount = stats.total?.membership_threshold_count ?? 0;
    const todayWashRewards = stats.today?.washes ?? 0;

    this.singleWashProgress = {
      current: currentWashCount,
      total: washCount,
      reward: `$${thresholdReward} for every ${washCount} memberships`,
      wonToday: `$${todayWashRewards.toFixed(2)} won today`,
    };

    const todayMembershipRewards = stats.today?.subscription ?? 0;
    this.membershipProgress = {
      reward: `$${membershipReward} for each membership`,
      description: 'Sell any membership.',
      wonToday: `$${todayMembershipRewards.toFixed(2)} won today`,
    };
  }

  private handleStatisticsError(error?: any): void {
    if (error) {
      console.error('Error loading reward statistics:', error);
    }
    this.resetToDefaultStatistics();
    this.showErrorMessage(ERROR_MESSAGES.STATISTICS);
  }

  private resetToDefaultStatistics(): void {
    this.lifetimeRewards = '$0.00';
    this.singleWashProgress = {
      current: 0,
      total: DEFAULT_REWARD_VALUES.WASH_COUNT,
      reward: `$${DEFAULT_REWARD_VALUES.THRESHOLD_REWARD} for every ${DEFAULT_REWARD_VALUES.WASH_COUNT} washes`,
      wonToday: '$0.00 won today',
    };
    this.membershipProgress = {
      reward: `$${DEFAULT_REWARD_VALUES.MEMBERSHIP_REWARD} for each membership`,
      description: 'Sell any membership.',
      wonToday: '$0.00 won today',
    };
  }

  private showErrorMessage(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: REWARDS_TOAST_LIFETIME,
    });
  }

  private formatCurrency(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }

  private loadRewardsHistory(): void {
    this.isLoading = true;
    const filters = this.getFiltersForCurrentPeriod();

    this.rewardsService
      .getRewardsHistory(filters)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: response => this.handleHistorySuccess(response),
        error: () => this.handleHistoryError(),
      });
  }

  private handleHistorySuccess(response: {
    history: RewardsHistoryItem[];
    pagination: RewardsPagination;
  }): void {
    this.rewardsHistory = response.history;
    this.updatePaginationData(response.pagination);

    if (this.selectedPeriod === 'Today') {
      this.calculateTodayRewardsFromHistory(response.history);
    }
  }

  private handleHistoryError(): void {
    this.rewardsHistory = [];
    this.paginationData = {
      ...REWARDS_DEFAULT_PAGINATION,
      itemsPerPage: this.paginationData.itemsPerPage,
    };
    this.showErrorMessage(ERROR_MESSAGES.HISTORY);
  }

  private filterDataByPeriod(): void {
    this.paginationData.currentPage = 1;
    this.loadRewardsHistory();
  }

  private getFiltersForCurrentPeriod(): RewardsFilters {
    const filters: RewardsFilters = {
      page: this.paginationData.currentPage,
      limit: this.paginationData.itemsPerPage,
    };

    const dateRange = getDateRangeForPeriod(this.selectedPeriod, this.customDateRange);
    if (dateRange) {
      filters.start_date = dateRange.start;
      filters.end_date = dateRange.end;
    }

    return filters;
  }

  private updatePaginationData(pagination?: RewardsPagination): void {
    if (pagination) {
      this.paginationData = {
        currentPage: pagination.page,
        totalPages: pagination.totalPages,
        totalItems: pagination.total,
        itemsPerPage: pagination.limit,
      };
    } else {
      this.paginationData.totalItems = this.rewardsHistory.length;
      this.paginationData.totalPages = Math.max(
        Math.ceil(this.paginationData.totalItems / this.paginationData.itemsPerPage),
        1
      );
    }
  }

  private calculateTodayRewardsFromHistory(history: RewardsHistoryItem[]): void {
    // Compare calendar YYYY-MM-DD strings directly — do not re-parse via `new Date('YYYY-MM-DD')`
    // (UTC midnight), which shifts the local day west of UTC and can miss today's row.
    const today = formatCalendarDateInTimeZone(new Date(), getUserTimezone());
    const todayItem = history.find(item => item.date === today);

    if (todayItem) {
      const singleWashToday = this.parseCurrency(todayItem.singleWashReward);
      const membershipToday = this.parseCurrency(todayItem.membershipReward);

      this.singleWashProgress.wonToday = `$${singleWashToday.toFixed(2)} won today`;
      this.membershipProgress.wonToday = `$${membershipToday.toFixed(2)} won today`;
    }
  }

  private parseCurrency(currencyString: string): number {
    const match = currencyString.match(/\$?([\d.]+)/);
    return match ? parseFloat(match[1]) : 0;
  }
}
